import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useSeamlessLogin from '@/hooks/useSeamlessLogin';
import { fetchMyProjects, fetchProject, setCurrentProjectId, updateEmailConfirmModal, updatePrevUrl, clearPermissions } from '@/actions';
import { AuthState } from '@/types';
import ErrorModal from '@/components/ErrorModal';
import ForceEmailConfirmModal from '@/components/ForceEmailConfirmModal';
import { useFetchPermissions } from '@/hooks/useFetchPermissions';
import { fetchAPI } from '@/api';
import { NotificationProvider } from '@/context/NotificationContext';
import SubscriptionNotifications from '@/components/SubscriptionNotifications';

interface GlobalWrapperProps {
    children: ReactNode;
}

// works on every page
export const GlobalWrapper: React.FC<GlobalWrapperProps> = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isSeamlessLogin, preLoggedIn, isLoginFailed } = useSeamlessLogin();
    const prevPathRef = useRef<string | null>(null);
    const auth = useSelector((state: { auth: AuthState }) => state.auth);
    const emailConfirmModalOpen = useSelector((state: any) => state.app.emailConfirmModal);
    const currentProjectId = useSelector((state: any) => state.project.currentId);
    const projectsById = useSelector((state: any) => state.project.byId);
    const myProjectsState = useSelector((state: any) => state.project.myIds);
    const isProjectsFailed = useSelector((state: any) => state.project.isProjectsFailed);

    const [email, setEmail] = useState<string>('');
    const [emailConfirmCode, setEmailConfirmCode] = useState<string>('');
    const [requestId, setRequestId] = useState<string | null>(null);
    const [errorModal, setErrorModal] = useState<string | null>(null);
    const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
    const [resendTimer, setResendTimer] = useState<number>(0);
    const [canResend, setCanResend] = useState<boolean>(true);
    const [hasResendBeenClicked, setHasResendBeenClicked] = useState<boolean>(false);
    const [isEmailVerifyLoading, setIsEmailVerifyLoading] = useState<boolean>(false);
    const [attemptedProjects, setAttemptedProjects] = useState<Set<string>>(new Set());

    const urlProjectId = searchParams.get("projectId");

    // Show loader for seamless login process
    const isSeamlessLoginLoading = isSeamlessLogin && !preLoggedIn && !isLoginFailed;

    // Show loader only if:
    // 1. Seamless login is in progress OR
    // 2. Projects are loading OR
    // 3. Project is not in store (not loaded yet)
    const isProjectLoading = urlProjectId && auth?.user?.token && (
        myProjectsState.isFetching ||
        !projectsById[urlProjectId]
    );

    const showLoader = isSeamlessLoginLoading || isProjectLoading;

    useFetchPermissions();

    const sendCodeEmail = async () => {
        try {
            setIsEmailVerifyLoading(true);
            setErrorModal(null);

            const response = await fetchAPI("/email/change", auth?.user?.token, {
                email
            });

            const data = await response.json();

            if (response.ok) {
                if (data.requestId) {
                    setRequestId(data.requestId);
                    setErrorModal(null);
                    setResendTimer(60);
                    setCanResend(false);
                } else {
                    dispatch(updateEmailConfirmModal(false));
                    window.location.reload();
                }
            } else {
                setErrorModal(data.message);
            }
        } catch (error) {
            console.error('Error creating recovery request:', error);
            setErrorModal('An error occurred, please try again later.');
        } finally {
            setIsEmailVerifyLoading(false);
        }
    }

    const onEmailResend = () => {
        setHasResendBeenClicked(true);

        onEmailSend();
    };

    const onEmailSend = () => {
        if (canResend) {
            setResendTimer(60);
            setCanResend(false);
            sendCodeEmail();
        }
    };

    const onEmailVerify = async () => {
        if (isEmailVerifyLoading) return;

        try {
            setIsEmailVerifyLoading(true);
            setErrorModal(null);

            const response = await fetchAPI("/email/change-verify", auth?.user?.token, {
                requestId,
                verificationCode: emailConfirmCode,
            });

            const data = await response.json();

            if (response.ok) {
                setErrorModal(null);
                dispatch(updateEmailConfirmModal(false));
                window.location.reload();
            } else {
                setErrorModal(data.message);
            }
        } catch (error: any) {
            console.error('Error verifying code:', error);
            setErrorModal(error?.response?.message || 'An error occurred, please try again later.');
        } finally {
            setIsEmailVerifyLoading(false);
        }
    };

    useEffect(() => {
        if (auth?.user?.token && !myProjectsState.isFetched && !myProjectsState.isFetching) {
            dispatch(fetchMyProjects(auth.user.token));
        }
        
        if (auth?.user?.token && currentProjectId && !projectsById[currentProjectId]) {
            dispatch(fetchProject(auth.user.token, currentProjectId));
        }
    }, [auth?.user?.token, currentProjectId, myProjectsState, projectsById]);

    useEffect(() => {
        if (errorModal) {
            setErrorModalOpen(true);
        }
    }, [errorModal]);

    useEffect(() => {
        if (!errorModalOpen) {
            setErrorModal(null);
        }
    }, [errorModalOpen]);

    useEffect(() => {
        if (prevPathRef.current) {
            dispatch(updatePrevUrl(prevPathRef.current));
        }
        prevPathRef.current = location.pathname;
    }, [location?.pathname]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (resendTimer > 0) {
            timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [resendTimer]);

    useEffect(() => {
        if (emailConfirmCode.length === 6) {
            onEmailVerify();
        }
    }, [emailConfirmCode.length]);

    useEffect(() => {
        const projectId = searchParams.get("projectId");
        
        if (!projectId || !auth?.user?.token) return;
        
        // Don't switch project while seamless login is in progress
        if (isSeamlessLogin && !preLoggedIn) return;
        
        if (myProjectsState.isFetching) return;

        // If we're already on the requested project, just remove the URL param
        if (currentProjectId === projectId) {
            setSearchParams({});
            return;
        }

        if (myProjectsState.isFetched && myProjectsState?.list && myProjectsState.list.indexOf(projectId) > -1) {
            if (currentProjectId !== projectId) {
                setSearchParams({});
                dispatch(setCurrentProjectId(projectId));
            }
        } else if (projectsById[projectId] && currentProjectId !== projectId) {
            // If project is loaded through fetchProject and is in the store - also switch
            setSearchParams({});
            dispatch(setCurrentProjectId(projectId));
        } else if (myProjectsState.isFetched && !projectsById[projectId] && !attemptedProjects.has(projectId)) {
            // If projects are loaded but project is not in the list - try to load directly
            dispatch(fetchProject(auth.user.token, projectId));
            // Mark that we tried to load this project
            setAttemptedProjects(prev => new Set([...prev, projectId]));
        }
    }, [myProjectsState, searchParams, currentProjectId, projectsById, auth?.user?.token, dispatch, attemptedProjects, isSeamlessLogin, preLoggedIn]);

    // Handle project loading errors
    useEffect(() => {
        const projectId = searchParams.get("projectId");
        
        if (!projectId || !auth?.user?.token) return;

        // Only proceed if myProjects loading is complete (either success or failure)
        if (myProjectsState.isFetching) return;

        // If fetchProject failed for this specific project
        if (isProjectsFailed && attemptedProjects.has(projectId)) {
            dispatch(clearPermissions());
            setSearchParams({});
            navigate('/select_project');
            return;
        }

        // If myProjects loading failed completely
        if (myProjectsState.isFailed) {
            dispatch(clearPermissions());
            setSearchParams({});
            navigate('/select_project');
            return;
        }

        // Only check project access after myProjects are successfully loaded
        // AND only if we attempted to load this project (meaning it wasn't in myProjects list)
        if (myProjectsState.isFetched && attemptedProjects.has(projectId)) {
            // If project is in myProjects list, don't redirect (it should have been switched already)
            if (myProjectsState.list?.includes(projectId)) {
                return;
            }
            
            // Project is not in user's project list AND not loaded in store
            if (!projectsById[projectId]) {
                dispatch(clearPermissions());
                setSearchParams({});
                navigate('/select_project');
            }
        }
    }, [isProjectsFailed, attemptedProjects, myProjectsState, projectsById, searchParams, auth?.user?.token, navigate, dispatch, currentProjectId]);

    if (showLoader) {
        return (
            <div className="h-screen flex items-center justify-center">
                <svg className="animate-rotation w-12 h-12 text-[#0D4E58] m-auto"
                     width="24"
                     height="25"
                     viewBox="0 0 24 25"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5"
                          stroke="#EAE6E0"
                          strokeWidth="4"
                          strokeLinecap="round" />
                    <path d="M12 2.5C11.4467 2.5 10.9039 2.54494 10.375 2.63136"
                          stroke="#EAE6E0"
                          strokeWidth="4"
                          strokeLinecap="round" />
                    <path d="M2 12.5C2 18.0228 6.47715 22.5 12 22.5C17.5228 22.5 22 18.0228 22 12.5C22 6.97715 17.5228 2.5 12 2.5"
                          stroke="currentColor"
                          strokeWidth="4"
                          strokeLinecap="round" />
                </svg>
            </div>
        );
    }

    return (
        <>
            <NotificationProvider>
                <SubscriptionNotifications />
                {children}
            </NotificationProvider>
            <ForceEmailConfirmModal
                modalOpen={emailConfirmModalOpen}
                setModalOpen={(isOpen) => dispatch(updateEmailConfirmModal(isOpen))}
                email={email}
                setEmail={setEmail}
                emailConfirmCode={emailConfirmCode}
                setEmailConfirmCode={setEmailConfirmCode}
                resendTimer={resendTimer}
                onResend={onEmailResend}
                onSend={onEmailSend}
                onVerify={onEmailVerify}
                hasResendBeenClicked={hasResendBeenClicked}
                isLoading={isEmailVerifyLoading || auth?.isLoggingIn}
                requestId={requestId}
            />
            <ErrorModal
                modalOpen={errorModalOpen}
                setModalOpen={setErrorModalOpen}
                error={errorModal}
            />
        </>
    );
};

